// Typed access to the item catalog and the unlock-gate rules.

import raw from '../data/items.json';
import type { GameState, Structure } from '../core/types';

export type ItemCategory =
  | 'coop'
  | 'fencing'
  | 'equipment'
  | 'nesting'
  | 'brooding'
  | 'storm'
  | 'litter'
  | 'feed'
  | 'decor'
  | 'service'
  | 'birds'
  | 'land';

export interface ItemEffects {
  providesWater?: boolean;
  providesFeed?: boolean;
  capacity?: number;
  chickSafe?: boolean;
  feedType?: 'starter' | 'grower' | 'layer';
  fills?: number;
  calcium?: boolean;
  grit?: boolean;
  cleansLitter?: number;
  coop?: boolean;
  shelter?: boolean;
  roof?: boolean;
  lockable?: boolean;
  nestBox?: boolean;
  perch?: boolean;
  fence?: boolean;
  heightInches?: number;
  brooder?: boolean;
  heatLamp?: boolean;
  quarantine?: boolean;
  footbath?: boolean;
  coveredFeeder?: boolean;
  coopLight?: boolean;
  decoyEgg?: boolean;
  shade?: boolean;
  /** Aviary netting over the run — see predators.ts breachChance. */
  overheadNet?: boolean;
  waterReserve?: boolean;
  stormPrep?: string;
  vetVisit?: boolean;
  spawnChick?: boolean;
  hatchingEgg?: boolean;
  expandGridTo?: number;
  decor?: boolean;
  /** Halves feed drain while placed — feed chore lasts twice as long. */
  autoFeed?: boolean;
  /** Halves water drain and fouling while placed — water chore lasts longer. */
  autoWater?: boolean;
  /** Auto-locks lockable coops at dusk. */
  autoLock?: boolean;
  /** Half-inch hardware cloth on the run: stops diggers and small gaps. */
  hardwareCloth?: boolean;
  /** A coop fixture (REFOCUS §7): buying it changes the coop, not the inventory. */
  fixture?: { slot: 'roof' | 'ventilation'; value: string };
  /**
   * Removes the deep-waterer hazard for chicks (PLAN-04 S6). The pebble hack
   * from Punto Pio's Field Notes, not a researched fix: INC-17 found no
   * published rate for the hazard and none for this either.
   */
  chickWaterSafety?: boolean;
}

/** What age a piece of gear is built for, in weeks (PLAN-04 S6). */
export interface AgeRange {
  minWeeks: number;
  /** Omitted means no upper limit. */
  maxWeeks?: number;
}

export interface StoreItem {
  id: string;
  name: { en: string; es: string };
  category: ItemCategory;
  price: number;
  gate: string;
  sprite: string;
  realCostUsd?: number;
  consumable?: boolean;
  /** Render footprint in tiles — sizing only. */
  footprint?: [number, number];
  /** Equipment installs into the yard on purchase instead of the inventory. */
  installed?: boolean;
  /** Waterers and feeders only: the ages the piece is built for (S6). */
  ageRange?: AgeRange;
  effects: ItemEffects;
}

export const ITEMS: StoreItem[] = raw.items as StoreItem[];

const BY_ID = new Map(ITEMS.map((item) => [item.id, item]));

export function getItem(id: string): StoreItem | undefined {
  return BY_ID.get(id);
}

export function itemsInCategory(category: ItemCategory): StoreItem[] {
  return ITEMS.filter((item) => item.category === category);
}

export function effectsOf(structure: Structure): ItemEffects {
  return getItem(structure.itemId)?.effects ?? {};
}

/** What age this piece of gear is built for, if it says. */
export function ageRangeOf(itemId: string): AgeRange | undefined {
  return getItem(itemId)?.ageRange;
}

/** True when the item carries the age rating at all — waterers and feeders. */
export function hasAgeRating(itemId: string): boolean {
  return ageRangeOf(itemId) !== undefined;
}

/** Fresh equipment arrives full and clean — the store sells it that way. */
export function initialStructureState(item: StoreItem): Structure['state'] {
  const s: Structure['state'] = {};
  const e = item.effects;
  // A reserve barrel arrives empty — filling it is the storm-prep chore, and
  // its whole lesson is "stock water *before* the storm". It still needs the
  // field initialised, or the storm checklist reads undefined and the
  // `stock_water` item can never be satisfied, which made `storm_ready` and so
  // the certificate unreachable.
  if (e.providesWater) {
    s.water = 100;
    s.waterPurity = 100;
  } else if (e.waterReserve) {
    s.water = 0;
    s.waterPurity = 100;
  }
  if (e.providesFeed) {
    s.feed = 0;
    s.feedType = e.feedType;
  }
  if (e.coop) {
    s.doorOpen = true;
    s.dirtiness = 0;
  }
  if (e.nestBox) s.eggs = [];
  if (e.brooder) s.lampSetting = 50;
  return s;
}

/**
 * Whether a gate is satisfied. Gates are named for the teachable moment that
 * opens them — the tall fence appears right after the first escape, when the
 * lesson has just landed.
 */
export function isUnlocked(gate: string, state: GameState): boolean {
  const p = state.progression;
  if (p.unlocks.includes(gate)) return true;

  switch (gate) {
    case 'always':
      return true;
    case 'tutorial':
    case 'tutorial_free':
    case 'tutorial_guided':
      return true;
    case 'post_tutorial':
      return p.tutorialComplete;
    case 'has_pullet':
      return state.flock.some((c) => c.stage === 'pullet' || c.stage === 'adult');
    case 'has_adult':
      return state.flock.some((c) => c.stage === 'adult');
    case 'has_coop':
      return state.structures.some((s) => effectsOf(s).coop);
    case 'four_birds':
      return state.flock.length >= 4;
    case 'four_birds_or_illness':
      return state.flock.length >= 4 || state.flock.some((c) => c.conditions.length > 0);
    case 'backyard':
      return state.housing === 'backyard';
    case 'bird_sick':
      return state.flock.some((c) => c.conditions.length > 0);
    case 'storm_system':
    case 'storm_watch_or_drill':
      return state.storm.active || state.weather.stormAlert !== 'none';
    case 'genetics_junior_or_code':
      return p.badges.includes('genetics_junior') || state.account.redeemedCodes.length > 0;
    case 'grid_full_6x6':
      return state.grid.width >= 6 && state.grid.width < 8;
    case 'grid_full_8x8':
      return state.grid.width >= 8 && state.grid.width < 10;
    // The rest are event-driven: something happens, then the item appears.
    default:
      return false;
  }
}

/** Human-readable hint shown on a locked card (UI-DESIGN.md §2). R4: every
 * gate has one — a bare "locked" with no reason was a teen quit trigger. */
export const GATE_HINTS: Record<string, { en: string; es: string }> = {
  has_pullet: {
    en: 'Unlocks when a chick grows into a pullet',
    es: 'Se desbloquea cuando un pollito crezca a polla',
  },
  has_adult: {
    en: 'Unlocks with your first adult hen',
    es: 'Se desbloquea con tu primera gallina adulta',
  },
  has_coop: {
    en: 'Unlocks once you place a coop',
    es: 'Se desbloquea cuando coloques un gallinero',
  },
  post_tutorial: {
    en: 'Unlocks when the tutorial is done (or skipped)',
    es: 'Se desbloquea al terminar el tutorial (o saltarlo)',
  },
  four_birds: {
    en: 'Unlocks with four birds in the flock',
    es: 'Se desbloquea con cuatro aves en la bandada',
  },
  four_birds_or_illness: {
    en: 'Unlocks with four birds — or sooner if one falls ill',
    es: 'Se desbloquea con cuatro aves — o antes si una enferma',
  },
  backyard: {
    en: 'Backyard yards only — balconies skip fences',
    es: 'Solo patios — los balcones no usan cercas',
  },
  bird_sick: {
    en: 'Appears when a bird needs care',
    es: 'Aparece cuando un ave necesita cuidado',
  },
  storm_system: {
    en: 'Unlocks when storms threaten',
    es: 'Se desbloquea cuando amenazan tormentas',
  },
  storm_watch_or_drill: {
    en: 'Unlocks during a storm watch — or run a drill',
    es: 'Se desbloquea con alerta de tormenta — o haz un simulacro',
  },
  genetics_junior_or_code: {
    en: 'Unlocks with the Genetics Junior badge — or redeem a code',
    es: 'Se desbloquea con la insignia de Genética — o canjea un código',
  },
  grid_full_6x6: {
    en: 'Unlocks when the 6×6 yard fills up',
    es: 'Se desbloquea cuando el patio 6×6 se llene',
  },
  grid_full_8x8: {
    en: 'Unlocks when the 8×8 yard fills up',
    es: 'Se desbloquea cuando el patio 8×8 se llene',
  },
  first_escape: {
    en: 'Unlocks after your first escape!',
    es: '¡Se desbloquea tras tu primera fuga!',
  },
  first_predator_attack: {
    en: 'Unlocks after something visits your yard at night',
    es: 'Se desbloquea cuando algo visita tu patio de noche',
  },
  first_heat_stress: {
    en: 'Unlocks after your first hot day',
    es: 'Se desbloquea tras tu primer día caluroso',
  },
  short_day_dip: {
    en: 'Unlocks when short days slow your eggs',
    es: 'Se desbloquea cuando los días cortos bajan la postura',
  },
  egg_eating_habit: {
    en: 'Unlocks if a hen starts eating eggs',
    es: 'Se desbloquea si una gallina empieza a comer huevos',
  },
  wild_bird_event: {
    en: 'Unlocks after wild birds visit your feed',
    es: 'Se desbloquea tras la visita de aves silvestres',
  },
  biosecurity_fact_seen: {
    en: 'Unlocks after the biosecurity lesson',
    es: 'Se desbloquea tras la lección de bioseguridad',
  },
};
